import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, TrendingUp, TrendingDown, Plus, Trophy, AlertTriangle } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { formatCurrency, formatPct } from "../lib/format";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useSeo } from "../lib/seo";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "profitable", label: "Profitable" },
  { key: "loss", label: "Loss-making" },
];

export default function Products() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("profit");
  const [filter, setFilter] = useState("all");

  useSeo({ title: "My Products | ProfitMigo", description: "Track and compare the profitability of your saved products.", path: "/products", robots: "noindex, follow" });

  const load = () => {
    api.get("/products")
      .then(({ data }) => setProducts(data))
      .catch(() => toast.error("Could not load your products. Please refresh."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      setProducts((p) => p.filter((x) => x.id !== id));
      toast.success("Product removed");
    } catch { toast.error("Could not remove"); }
  };

  const profitable = products.filter((p) => p.net_profit >= 0);
  const losses = products.filter((p) => p.net_profit < 0);

  const bestId = useMemo(() => {
    if (!products.length) return null;
    return products.reduce((a, b) => (b.net_profit > a.net_profit ? b : a)).id;
  }, [products]);
  const worstId = useMemo(() => {
    if (!products.length) return null;
    return products.reduce((a, b) => (b.net_profit < a.net_profit ? b : a)).id;
  }, [products]);

  const view = useMemo(() => {
    let list = products;
    if (filter === "profitable") list = list.filter((p) => p.net_profit >= 0);
    if (filter === "loss") list = list.filter((p) => p.net_profit < 0);
    const sorted = [...list].sort((a, b) => {
      if (sortBy === "margin") return b.margin_pct - a.margin_pct;
      if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
      return b.net_profit - a.net_profit;
    });
    return sorted;
  }, [products, filter, sortBy]);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "My Products" }]} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">My Products</h1>
          <p className="mt-1 text-sm text-muted-foreground">Signed in as {user?.email}</p>
        </div>
        <Button asChild variant="outline" data-testid="add-product-cta">
          <Link to="/ecommerce/product-profit-calculator"><Plus className="mr-2 h-4 w-4" />Add from a calculator</Link>
        </Button>
      </div>

      {!loading && products.length > 0 && (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-border bg-card p-4"><div className="text-xs uppercase text-muted-foreground">Products</div><div className="font-mono text-2xl font-semibold">{products.length}</div></div>
            <div className="rounded-lg border border-border bg-card p-4"><div className="text-xs uppercase text-muted-foreground">Profitable</div><div className="font-mono text-2xl font-semibold text-emerald-600">{profitable.length}</div></div>
            <div className="rounded-lg border border-border bg-card p-4"><div className="text-xs uppercase text-muted-foreground">Loss-making</div><div className="font-mono text-2xl font-semibold text-red-600">{losses.length}</div></div>
          </div>

          {/* Catalogue compare controls */}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-1" data-testid="product-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`filter-${f.key}`}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${filter === f.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Sort by</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40" data-testid="product-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="profit">Net profit</SelectItem>
                  <SelectItem value="margin">Margin</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}

      {loading ? (
        <p className="mt-10 text-muted-foreground">Loading…</p>
      ) : products.length === 0 ? (
        <div className="mt-10 rounded-xl border border-dashed border-border p-10 text-center" data-testid="products-empty">
          <p className="text-muted-foreground">No saved products yet.</p>
          <Button asChild className="mt-4"><Link to="/ecommerce/product-profit-calculator">Calculate and save a product</Link></Button>
        </div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="products-list">
          {view.map((p) => {
            const loss = p.net_profit < 0;
            const isBest = p.id === bestId && p.net_profit > 0 && products.length > 1;
            const isWorst = p.id === worstId && p.id !== bestId && p.net_profit < 0;
            return (
              <div key={p.id} className="flex items-center justify-between rounded-lg border border-border bg-card p-4" data-testid={`product-${p.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {loss ? <TrendingDown className="h-4 w-4 text-red-500" /> : <TrendingUp className="h-4 w-4 text-emerald-500" />}
                    <span className="truncate font-medium">{p.name}</span>
                    {isBest && <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid={`best-${p.id}`}><Trophy className="h-3 w-3" />Best</span>}
                    {isWorst && <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700" data-testid={`worst-${p.id}`}><AlertTriangle className="h-3 w-3" />Worst</span>}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{p.marketplace || p.calculator}</div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className={`font-mono font-semibold ${loss ? "text-red-600" : "text-emerald-600"}`}>{formatCurrency(p.net_profit)}</div>
                    <div className="text-xs text-muted-foreground">{formatPct(p.margin_pct)} margin</div>
                  </div>
                  <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive" aria-label="Delete" data-testid={`delete-${p.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
